import type { FC } from 'react';

import { useContent } from '@eneco/cms';
import { Card, Grid, Heading, RatingStar, Stack, Text } from '@eneco/sparky';

import { gridColumns } from './gridColumns';
import type { Testimonial, TestimonialsContent } from './testimonials-content.types';

const MAX_STARS = 5;
/** The live payload uses a 1-5 star scale; `RatingStar` expects 0-10. */
const toRatingStarScore = (rating: number): number => rating * 2;

/**
 * `testimonials` block for the CMS-agnostic lane. POC-schema-only (see
 * `testimonials-content.types.ts`) — flat text/number fields, no `adapter.*` mapping needed.
 */
const Testimonials: FC = () => {
  const content = useContent<TestimonialsContent>();
  const items = content.items ?? [];

  if (items.length === 0) return null;

  return (
    <Stack gap="6">
      {content.title && <Heading as="h2">{content.title}</Heading>}
      <Grid columns={{ initial: 1, md: gridColumns(items.length) }} gap="6">
        {items.map(item => (
          <TestimonialCard key={item._key} testimonial={item} />
        ))}
      </Grid>
    </Stack>
  );
};

const TestimonialCard: FC<{ testimonial: Testimonial }> = ({ testimonial }) => (
  <Card>
    <Card.Content>
      <Stack gap="4">
        {typeof testimonial.rating === 'number' && <RatingStar maxStars={MAX_STARS} score={toRatingStarScore(testimonial.rating)} />}
        {testimonial.quote && <Text as="p">&ldquo;{testimonial.quote}&rdquo;</Text>}
        {(testimonial.author || testimonial.source) && (
          <Text size="BodyS" color="textSecondary">
            {[testimonial.author, testimonial.source].filter(Boolean).join(' — ')}
          </Text>
        )}
      </Stack>
    </Card.Content>
  </Card>
);

export default Testimonials;
